import { useEffect, useState } from "react";
import {
  Banner,
  BlockStack,
  Button,
  Card,
  Checkbox,
  DropZone,
  FormLayout,
  InlineStack,
  Page,
  Select,
  Tag,
  Spinner,
  Text,
  TextField,
} from "@shopify/polaris";
import { useNavigate } from "react-router-dom";
import AppWrapper from "../AppWrapper";
import supabase from "../../supabase-client";
import { formatPlate } from "../../utility/funcs";
import { useBranch } from "../../context/BranchContext";
import { UserAuth } from "../../context/AuthContext";
import PayeePicker from "./PayeePicker";
import CarPicker from "./CarPicker";
import CategorySelect, { useEgresoCategories } from "./CategorySelect";
import { NO_INVOICE_OPTIONS, TRIGGER_LABEL, money, rpcError, toCents, uploadEgresoDoc } from "../../utility/egresos";

// Nuevo egreso. No type to choose: every egreso is the same thing (who we pay,
// for what, how much, which car), only the order of events differs.
//
// Optional document first. A CFDI XML is read exactly here (no AI) and, once
// the egreso exists, goes through gastos_inbox like any received invoice (SAT
// validity, EFOS) and becomes its first invoice. A PDF/photo (cotización, PDF
// factura, ticket) is read by parse_egreso_document and only SUGGESTS:
//   - payee: applied only on a single exact RFC match; otherwise the document's
//     name/RFC/CLABE prefill a "Nuevo tercero" form (bank-verified before SPEI);
//   - car: applied only on a single exact plate/VIN match in the fleet;
//   - title, category, agreed amount: prefilled, editable.
// Payments are never inferred: the plan is added by hand.
//
// Someone with only egresos.request uses this same form as a payment request
// ("Nueva solicitud de pago"): the amount is required, there is no payment
// plan, and the document is kept as the request's attachment (registering a
// CFDI is for staff with services.create). Whoever can pay approves it by
// paying it, or rejects it.

const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] || "");
    r.onerror = reject;
    r.readAsDataURL(file);
  });

// A CFDI 3.3/4.0 XML, read exactly. Null if it isn't one.
async function readCfdiXml(file) {
  const doc = new DOMParser().parseFromString(await file.text(), "application/xml");
  const one = (tag) => doc.getElementsByTagNameNS("*", tag)[0];
  const root = one("Comprobante");
  const timbre = one("TimbreFiscalDigital");
  if (!root || !timbre) return null;
  const emisor = one("Emisor");
  const conceptos = [...doc.getElementsByTagNameNS("*", "Concepto")].map((c) => c.getAttribute("Descripcion")).filter(Boolean);
  return {
    uuid: timbre.getAttribute("UUID"),
    tipo: root.getAttribute("TipoDeComprobante"),
    total: Number(root.getAttribute("Total")),
    folio: [root.getAttribute("Serie"), root.getAttribute("Folio")].filter(Boolean).join("-"),
    metodo: root.getAttribute("MetodoPago"),
    issuer: { name: emisor?.getAttribute("Nombre") || null, rfc: (emisor?.getAttribute("Rfc") || "").toUpperCase() || null },
    receptorRfc: (one("Receptor")?.getAttribute("Rfc") || "").toUpperCase(),
    title: conceptos[0] ? conceptos[0].slice(0, 80) : null,
  };
}

// The one profile with exactly this RFC, or null (none, or more than one).
async function payeeByRfc(rfc) {
  if (!rfc) return null;
  const { data } = await supabase.rpc("find_party_matches", { p_rfc: rfc });
  const hits = [...new Map((data || []).filter((m) => m.exact && m.matched_on === "RFC").map((m) => [`${m.party_type}:${m.party_id}`, m])).values()];
  return hits.length === 1
    ? { payee_type: hits[0].party_type, payee_id: hits[0].party_id, name: hits[0].name, detail: `RFC ${rfc}` }
    : null;
}

// The one fleet car with exactly this plate or VIN, or null.
async function carByPlateOrVin({ plate, vin }) {
  const ors = [plate && `plate.eq.${plate}`, vin && `vin.eq.${vin}`].filter(Boolean);
  if (!ors.length) return null;
  const { data } = await supabase.from("cars").select("id, plate, vin, year, model:car_models(name)").or(ors.join(",")).limit(2);
  return data?.length === 1 ? data[0] : null;
}

const isXml = (f) => /\.xml$/i.test(f.name);

export default function NewEgreso() {
  const navigate = useNavigate();
  const { branch } = useBranch();
  const { hasPermission } = UserAuth();
  const isRequest = !hasPermission?.("services.create");

  const [files, setFiles] = useState([]);
  const [reading, setReading] = useState(false);
  const [doc, setDoc] = useState(null); // { kind: "cfdi" | "documento", label, warnings, xml? }
  const [docError, setDocError] = useState(null);

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [categories, reloadCategories] = useEgresoCategories();
  const [payee, setPayee] = useState(null);
  const [payeePrefill, setPayeePrefill] = useState(null);
  const [car, setCar] = useState(null);
  const [client, setClient] = useState(null);
  const [clientQuery, setClientQuery] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [committed, setCommitted] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [plan, setPlan] = useState([]);
  const [notes, setNotes] = useState("");
  const [noInvoice, setNoInvoice] = useState(false);
  const [noInvoiceReason, setNoInvoiceReason] = useState("");
  const [noInvoiceNote, setNoInvoiceNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (clientQuery.trim().length < 2) return setClientResults([]);
    const t = setTimeout(async () => {
      const { data } = await supabase.rpc("search_clients", { search_text: clientQuery });
      setClientResults((data || []).slice(0, 5));
    }, 250);
    return () => clearTimeout(t);
  }, [clientQuery]);

  // Read the dropped document(s) and prefill what is certain.
  //
  // Drops ADD to the files: the PDF first and its XML later is the normal
  // order, and a second drop must not throw away a file already read. Each
  // file is read once. An XML is exact, so it wins over what the AI read from
  // a PDF; nothing already filled in is overwritten.
  const readDocument = async (dropped) => {
    const added = dropped.filter((f) => !files.some((g) => g.name === f.name && g.size === f.size));
    if (!added.length) return;
    const all = [...files, ...added];
    setFiles(all);
    setDocError(null);
    const xmlFile = added.find(isXml);
    // Nothing new to read: an extra PDF/photo next to an XML or a document already read.
    if (!xmlFile && (doc || all.some(isXml))) return;
    setReading(true);
    try {
      let found;
      if (xmlFile) {
        const x = await readCfdiXml(xmlFile);
        if (!x) throw new Error("El XML no es un CFDI timbrado.");
        const warnings = [];
        if (x.receptorRfc && x.receptorRfc !== "GOM240618BW7") warnings.push(`La factura está a nombre de ${x.receptorRfc}, no de GoMove: no será deducible.`);
        if (x.tipo !== "I") warnings.push("Es un CFDI de tipo " + x.tipo + " (no de ingreso).");
        found = { issuer: x.issuer, title: x.title, total: x.total, payment: {}, vehicle: {}, category: null };
        setDoc({ kind: "cfdi", file: xmlFile, xml: x, label: `Factura ${x.folio || x.uuid.slice(0, 8)} de ${x.issuer.name || x.issuer.rfc} · ${money(Math.round(x.total * 100))}`, warnings });
      } else {
        const f = added[0];
        const mediaType = f.type || (/\.pdf$/i.test(f.name) ? "application/pdf" : "");
        const { data, error: err } = await supabase.functions.invoke("parse_egreso_document", {
          body: { file: await toBase64(f), media_type: mediaType, categories },
        });
        if (err) {
          let msg = err.message;
          try {
            msg = (await err.context.json()).error || msg;
          } catch {
            /* keep */
          }
          throw new Error(msg);
        }
        found = data;
        const what = { cotizacion: "Cotización", factura: "Factura (PDF)", ticket: "Ticket", otro: "Documento" }[data.doc_type] || "Documento";
        const warnings = [...(data.warnings || [])];
        if (data.doc_type === "factura") warnings.unshift("Es el PDF de una factura: sube también su XML para registrarla como factura.");
        setDoc({
          kind: "documento",
          file: f,
          docType: data.doc_type,
          label: `${what}${data.issuer?.name ? ` de ${data.issuer.name}` : ""}${data.total ? ` · ${money(Math.round(data.total * 100))}` : ""}`,
          warnings,
        });
      }

      if (found.title && !title) setTitle(found.title);
      // Only a category from the list; the reader may not invent one.
      if (found.category && !category && categories.includes(found.category)) setCategory(found.category);
      // Agreed amount: from a quote. An invoice IS the cost; no separate agreement needed.
      if (found.total && !xmlFile && !committed) setCommitted(String(found.total));
      if (!payee && found.issuer?.rfc) {
        const match = await payeeByRfc(found.issuer.rfc);
        if (match) setPayee(match);
        else if (found.issuer.name)
          setPayeePrefill({
            name: found.issuer.name,
            rfc: found.issuer.rfc,
            clabe: found.payment?.clabe || "",
            banco: found.payment?.bank || "",
          });
      } else if (!payee && found.issuer?.name) {
        setPayeePrefill({ name: found.issuer.name, clabe: found.payment?.clabe || "", banco: found.payment?.bank || "" });
      }
      if (!car && (found.vehicle?.plate || found.vehicle?.vin)) {
        const c = await carByPlateOrVin(found.vehicle);
        if (c) setCar(c);
      }
    } catch (e) {
      setDocError(e.message || "No se pudo leer el documento");
    }
    setReading(false);
  };

  // Removing the file a reading came from drops that reading (what it filled
  // in stays); removing any other file keeps it.
  const removeFile = (f) => {
    setFiles((list) => list.filter((g) => g !== f));
    if (doc?.file === f) setDoc(null);
    setDocError(null);
  };

  const committedCents = committed ? toCents(committed) : null;
  const planCents = plan.reduce((s, p) => s + (toCents(p.amount) || 0), 0);
  const planMismatch = plan.length > 0 && committedCents && planCents !== committedCents;
  const splitHalf = () => {
    if (!committedCents) return;
    const first = Math.round(committedCents / 2);
    setPlan([
      { label: "Anticipo 50%", amount: (first / 100).toFixed(2), trigger: "al_pedir" },
      { label: "Resto 50%", amount: ((committedCents - first) / 100).toFixed(2), trigger: "contra_entrega" },
    ]);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    const p = {
      title,
      category,
      branch_id: branch?.id || null,
      payee_type: payee?.payee_type,
      payee_id: payee?.payee_id,
      customer_id: client && client.client_type !== "company" ? client.id : null,
      company_id: client && client.client_type === "company" ? client.id : null,
      car_id: car?.id || null,
      committed_cents: committedCents || null,
      due_date: dueDate || null,
      notes,
      no_invoice_reason: noInvoice ? noInvoiceReason : null,
      no_invoice_note: noInvoice ? noInvoiceNote : null,
      planned_payments: plan.map((x) => ({ label: x.label, amount_cents: toCents(x.amount), trigger: x.trigger })),
    };
    const { data: id, error: err } = await supabase.rpc("create_egreso", { p });
    if (err) {
      setSaving(false);
      return setError(rpcError(err));
    }

    // The egreso exists; attaching the document must not lose it if it fails.
    const problems = [];
    try {
      if (doc?.kind === "cfdi" && !isRequest) {
        const payload = await Promise.all(files.map(async (f) => ({ name: f.name, base64: await toBase64(f) })));
        const { error: upErr } = await supabase.functions.invoke("gastos_inbox/upload", { body: { files: payload } });
        if (upErr) throw new Error("No se pudo registrar la factura");
        const { data: row } = await supabase.from("expense_cfdis").select("id, status").ilike("uuid", doc.xml.uuid).maybeSingle();
        if (!row || row.status !== "unmatched") problems.push("La factura ya estaba asignada a otro gasto o fue rechazada.");
        else {
          const { error: e2 } = await supabase.rpc("add_egreso_entry", { p_egreso: id, p: { kind: "cost", expense_cfdi_id: row.id } });
          if (e2) problems.push(rpcError(e2));
        }
      } else if (files[0]) {
        // A request keeps the readable file (the PDF of an XML+PDF pair).
        const file = files.find((f) => !/\.xml$/i.test(f.name)) || files[0];
        const path = await uploadEgresoDoc(id, doc?.docType === "ticket" ? "ticket" : "cotizacion", file);
        await supabase.rpc("update_egreso", { p_id: id, p: { quote_path: path } });
      }
    } catch (e) {
      problems.push(e.message || "No se pudo adjuntar el documento");
    }
    navigate(`/egresos/${id}`, { state: problems.length ? { problems } : undefined });
  };

  return (
    <AppWrapper>
      <Page
        title={isRequest ? "Nueva solicitud de pago" : "Nuevo egreso"}
        backAction={{ content: isRequest ? "Mis solicitudes" : "Egresos", url: "/egresos" }}
        narrowWidth
      >
        <BlockStack gap="400">
          {error && <Banner tone="critical">{error}</Banner>}

          <Card>
            <BlockStack gap="300">
              <DropZone
                label="Cotización o factura"
                allowMultiple
                disabled={reading}
                onDrop={(_a, accepted) => readDocument(accepted)}
                accept=".pdf,.xml,image/*"
              >
                {reading ? (
                  <div style={{ padding: 16, textAlign: "center" }}>
                    <InlineStack gap="200" align="center" blockAlign="center">
                      <Spinner size="small" />
                      <Text tone="subdued">Leyendo…</Text>
                    </InlineStack>
                  </div>
                ) : (
                  <DropZone.FileUpload
                    actionHint={files.length ? "Agrega el XML o el PDF que falte." : "PDF, XML (con su PDF) o foto. Llenamos lo que se pueda."}
                  />
                )}
              </DropZone>
              {files.length > 0 && (
                <InlineStack gap="100">
                  {files.map((f) => (
                    <Tag key={`${f.name}:${f.size}`} onRemove={reading ? undefined : () => removeFile(f)}>
                      {f.name}
                    </Tag>
                  ))}
                </InlineStack>
              )}
              {docError && <Text tone="critical">{docError}</Text>}
              {doc && (
                <BlockStack gap="100">
                  <Text fontWeight="semibold">{doc.label}</Text>
                  {doc.warnings.map((w, i) => (
                    <Text key={i} tone="caution" variant="bodySm">{w}</Text>
                  ))}
                </BlockStack>
              )}
            </BlockStack>
          </Card>

          <Card>
            <FormLayout>
              <TextField label="¿Qué es?" value={title} onChange={setTitle} autoComplete="off" placeholder="Escritorio para oficina" requiredIndicator />
              <CategorySelect value={category} onChange={setCategory} categories={categories} onAdded={reloadCategories} requiredIndicator />
              <PayeePicker value={payee} onChange={setPayee} prefill={payee ? null : payeePrefill} />
              {doc?.kind !== "cfdi" && (
                <BlockStack gap="200">
                  <Checkbox
                    label="No lleva factura"
                    checked={noInvoice}
                    onChange={setNoInvoice}
                  />
                  {noInvoice && (
                    <FormLayout.Group>
                      <Select label="Motivo" options={NO_INVOICE_OPTIONS} value={noInvoiceReason} onChange={setNoInvoiceReason} requiredIndicator />
                      {noInvoiceReason === "otro" && (
                        <TextField label="¿Por qué?" value={noInvoiceNote} onChange={setNoInvoiceNote} autoComplete="off" requiredIndicator />
                      )}
                    </FormLayout.Group>
                  )}
                </BlockStack>
              )}
            </FormLayout>
          </Card>

          <Card>
            <BlockStack gap="300">
              <Text variant="headingSm">Auto</Text>
              {car ? (
                <InlineStack gap="200" blockAlign="center">
                  <Text>
                    {formatPlate(car.plate)} · {car.model?.name} {car.year}
                  </Text>
                  <Button variant="plain" onClick={() => setCar(null)}>Quitar</Button>
                </InlineStack>
              ) : (
                <CarPicker label="Auto de la flota" onPick={setCar} />
              )}
              <Text variant="headingSm">Cliente</Text>
              {client ? (
                <InlineStack gap="200" blockAlign="center">
                  <Text>{client.name}</Text>
                  <Button variant="plain" onClick={() => setClient(null)}>Quitar</Button>
                </InlineStack>
              ) : (
                <BlockStack gap="100">
                  <TextField
                    label="Si el gasto es por un cliente"
                    value={clientQuery}
                    onChange={setClientQuery}
                    autoComplete="off"
                    placeholder="Buscar cliente o empresa"
                  />
                  {clientResults.map((c) => (
                    <Button key={`${c.client_type}:${c.id}`} variant="plain" textAlign="left" onClick={() => setClient(c)}>
                      {c.name}
                      {c.detail ? ` · ${c.detail}` : ""}
                    </Button>
                  ))}
                </BlockStack>
              )}
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="300">
              <FormLayout.Group>
                <TextField
                  label={isRequest ? "Monto" : "Monto acordado"}
                  value={committed}
                  onChange={setCommitted}
                  prefix="$"
                  type="number"
                  autoComplete="off"
                  requiredIndicator={isRequest}
                  helpText={isRequest ? undefined : doc?.kind === "cfdi" ? "Con factura no hace falta: el monto es el de la factura." : "Total con IVA de la cotización"}
                />
                <TextField label="Fecha de pago" value={dueDate} onChange={setDueDate} type="date" autoComplete="off" />
              </FormLayout.Group>
              {!isRequest && plan.map((x, i) => (
                <FormLayout.Group key={i} condensed>
                  <TextField label="Pago" value={x.label} onChange={(v) => setPlan((p) => p.map((y, j) => (j === i ? { ...y, label: v } : y)))} autoComplete="off" />
                  <TextField label="Monto" prefix="$" type="number" value={x.amount} onChange={(v) => setPlan((p) => p.map((y, j) => (j === i ? { ...y, amount: v } : y)))} autoComplete="off" />
                  <Select
                    label="Cuándo"
                    options={Object.entries(TRIGGER_LABEL).map(([value, label]) => ({ value, label }))}
                    value={x.trigger}
                    onChange={(v) => setPlan((p) => p.map((y, j) => (j === i ? { ...y, trigger: v } : y)))}
                  />
                </FormLayout.Group>
              ))}
              {!isRequest && <InlineStack gap="200">
                <Button onClick={() => setPlan((p) => [...p, { label: `Pago ${p.length + 1}`, amount: "", trigger: "fecha" }])}>
                  Agregar pago al plan
                </Button>
                {committedCents > 0 && plan.length === 0 && <Button variant="plain" onClick={splitHalf}>50% / 50%</Button>}
                {plan.length > 0 && <Button variant="plain" tone="critical" onClick={() => setPlan([])}>Quitar plan</Button>}
              </InlineStack>}
              {planMismatch && <Text tone="caution">Los pagos suman {money(planCents)} y el monto acordado es {money(committedCents)}.</Text>}
            </BlockStack>
          </Card>

          <Card>
            <TextField label="Notas" value={notes} onChange={setNotes} multiline={2} autoComplete="off" />
          </Card>

          <InlineStack align="end">
            <Button
              variant="primary"
              loading={saving}
              disabled={
                !title.trim() ||
                !category ||
                !payee ||
                planMismatch ||
                reading ||
                (isRequest && !(committedCents > 0)) ||
                (noInvoice && (!noInvoiceReason || (noInvoiceReason === "otro" && !noInvoiceNote.trim())))
              }
              onClick={save}
            >
              {isRequest ? "Enviar solicitud" : "Crear egreso"}
            </Button>
          </InlineStack>
        </BlockStack>
      </Page>
    </AppWrapper>
  );
}
